// Posts Page with Language Analysis - Extracted from Dashboard.jsx
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { Languages, ExternalLink, Filter, X, Calendar, ArrowUpDown, MessageSquareText } from "lucide-react";
import { translations, getPostUrl } from "@/i18n/translations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { API, getPlatformBadgeClass, formatNumber } from "./constants";
import LanguageAnalysisPanel from "./LanguageAnalysisPanel";

// Platform definitions
const ALL_PLATFORMS = [
  { id: "youtube", name: "YouTube", color: "#FF0000" },
  { id: "tiktok", name: "TikTok", color: "#00F2EA" },
  { id: "instagram", name: "Instagram", color: "#E4405F" },
  { id: "douyin", name: "Douyin", color: "#1C1C1E" },
  { id: "xiaohongshu", name: "小红书", color: "#FE2C55" },
  { id: "bilibili", name: "Bilibili", color: "#00A1D6" },
  { id: "weibo", name: "Weibo", color: "#E6162D" },
];

// Mobile Post Card for responsive display
const MobilePostCard = ({ post, lang, accounts, onAnalyze, onCommentAnalyze, t }) => {
  const getUsername = (accountId) => {
    const account = accounts.find(a => a.id === accountId);
    return account?.username || "";
  };
  const postUrl = post.post_url || getPostUrl(post.platform, post.post_id, getUsername(post.account_id));
  const canAnalyze = post.platform === "youtube" && post.comments > 0;
  const canCommentAnalyze = post.comments > 0;
  const isChinese = ["douyin", "xiaohongshu", "weibo", "bilibili"].includes(post.platform);
  const showViewsNA = isChinese && post.views === 0;

  return (
    <div className="p-3 rounded-lg border border-border/30 hover:border-border/60 transition-colors space-y-2" data-testid={`post-card-${post.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{post.title || "Untitled"}</p>
          <Badge variant="outline" className={`${getPlatformBadgeClass(post.platform)} text-xs capitalize mt-1`}>
            {post.platform}
          </Badge>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {canCommentAnalyze && (
            <Button variant="ghost" size="sm" onClick={() => onCommentAnalyze(post)} className="h-8 w-8 p-0 text-emerald-400" title={lang === "ko" ? "댓글 분석" : lang === "zh" ? "评论分析" : "Comment Analysis"} data-testid={`comment-analyze-btn-${post.id}`}>
              <MessageSquareText className="h-4 w-4" />
            </Button>
          )}
          {canAnalyze && (
            <Button variant="ghost" size="sm" onClick={() => onAnalyze(post)} className="h-8 w-8 p-0 text-cyan-400" data-testid={`analyze-btn-${post.id}`}>
              <Languages className="h-4 w-4" />
            </Button>
          )}
          {postUrl && (
            <a href={postUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted text-primary" data-testid={`post-link-${post.id}`}>
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{t.posts.viewsHeader}</span>
          <span className="font-mono font-medium">{showViewsNA ? "N/A" : formatNumber(post.views)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{t.posts.likesHeader}</span>
          <span className={`font-mono font-medium ${isChinese ? "text-emerald-500" : ""}`}>{formatNumber(post.likes || 0)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{t.posts.commentsHeader}</span>
          <span className="font-mono font-medium">{formatNumber(post.comments)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{t.posts.engagementHeader}</span>
          <span className={`font-mono font-medium ${post.engagement_rate > 5 ? "text-emerald-500" : post.engagement_rate > 2 ? "text-yellow-500" : ""}`}>
            {post.engagement_rate?.toFixed(2) || "0.00"}%
          </span>
        </div>
      </div>
    </div>
  );
};

// Enhanced Posts Table with Analyze button
const PostsTableWithAnalysis = ({ posts, loading, lang = "en", accounts = [], onAnalyze, onCommentAnalyze }) => {
  const t = translations[lang] || translations.en;
  
  const getUsername = (accountId) => {
    const account = accounts.find(a => a.id === accountId);
    return account?.username || "";
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="skeleton h-12 rounded-md" />
        ))}
      </div>
    );
  }

  const commentAnalyzeLabel = lang === "ko" ? "댓글 분석" : lang === "zh" ? "评论分析" : "Comments";

  return (
    <>
      {/* Mobile: Card Layout */}
      <div className="md:hidden">
        <div className="h-[500px] overflow-y-auto space-y-2 pr-1">
          {posts.map((post) => (
            <MobilePostCard key={post.id} post={post} lang={lang} accounts={accounts} onAnalyze={onAnalyze} onCommentAnalyze={onCommentAnalyze} t={t} />
          ))}
        </div>
      </div>

      {/* Desktop: Table Layout */}
      <div className="hidden md:block">
        <ScrollArea className="h-[500px]">
          <Table>
            <TableHeader>
              <TableRow className="border-border/50 hover:bg-transparent">
                <TableHead className="text-muted-foreground font-mono text-xs uppercase">{t.posts.post}</TableHead>
                <TableHead className="text-muted-foreground font-mono text-xs uppercase">{t.posts.platform}</TableHead>
                <TableHead className="text-right text-muted-foreground font-mono text-xs uppercase">{t.posts.viewsHeader}</TableHead>
                <TableHead className="text-right text-muted-foreground font-mono text-xs uppercase">{t.posts.likesHeader}</TableHead>
                <TableHead className="text-right text-muted-foreground font-mono text-xs uppercase">{t.posts.commentsHeader}</TableHead>
                <TableHead className="text-right text-muted-foreground font-mono text-xs uppercase">{t.posts.engagementHeader}</TableHead>
                <TableHead className="text-center text-muted-foreground font-mono text-xs uppercase">{commentAnalyzeLabel}</TableHead>
                <TableHead className="text-center text-muted-foreground font-mono text-xs uppercase">{t.languageAnalysis.title}</TableHead>
                <TableHead className="text-center text-muted-foreground font-mono text-xs uppercase">{t.posts.linkHeader}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {posts.map((post) => {
                const postUrl = post.post_url || getPostUrl(post.platform, post.post_id, getUsername(post.account_id));
                const canAnalyze = post.platform === "youtube" && post.comments > 0;
                const canCommentAnalyze = post.comments > 0;
                const isChinese = ["douyin", "xiaohongshu", "weibo", "bilibili"].includes(post.platform);
                return (
                  <TableRow key={post.id} className="table-row-hover border-border/30" data-testid={`post-row-${post.id}`}>
                    <TableCell className="font-medium max-w-[250px] truncate">{post.title || "Untitled"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${getPlatformBadgeClass(post.platform)} text-xs capitalize`}>
                        {post.platform}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {isChinese && post.views === 0 ? (
                        <span className="text-muted-foreground text-xs">N/A</span>
                      ) : (
                        formatNumber(post.views)
                      )}
                    </TableCell>
                    <TableCell className={`text-right font-mono ${isChinese ? "text-emerald-500 font-semibold" : ""}`}>
                      {formatNumber(post.likes || 0)}
                    </TableCell>
                    <TableCell className="text-right font-mono">{formatNumber(post.comments)}</TableCell>
                    <TableCell className="text-right">
                      <span className={`font-mono ${post.engagement_rate > 5 ? "text-emerald-500" : post.engagement_rate > 2 ? "text-yellow-500" : "text-muted-foreground"}`}>
                        {post.engagement_rate?.toFixed(2) || "0.00"}%
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {canCommentAnalyze ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onCommentAnalyze(post)}
                          className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                          data-testid={`comment-analyze-btn-${post.id}`}
                        >
                          <MessageSquareText className="h-4 w-4" />
                        </Button>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {canAnalyze ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onAnalyze(post)}
                          className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                          data-testid={`analyze-btn-${post.id}`}
                        >
                          <Languages className="h-4 w-4" />
                        </Button>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {postUrl ? (
                        <a
                          href={postUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center p-1.5 rounded-md hover:bg-muted transition-colors text-primary hover:text-primary/80"
                          title={t.posts.openOriginal}
                          data-testid={`post-link-${post.id}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>
    </>
  );
};

// Main Posts Page with Analysis Component
const PostsPageWithAnalysis = ({ lang = "en", startDate: propStartDate, endDate: propEndDate, selectedPlatforms: propPlatforms = [], selectedAccounts = [] }) => {
  const t = translations[lang] || translations.en;
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  
  // Local filter states
  const [localPlatforms, setLocalPlatforms] = useState(propPlatforms.length > 0 ? propPlatforms : []);
  const [localStartDate, setLocalStartDate] = useState(propStartDate || null);
  const [localEndDate, setLocalEndDate] = useState(propEndDate || null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState("published_at");

  // Sync with parent date props when they change
  useEffect(() => {
    if (propStartDate) setLocalStartDate(propStartDate);
    if (propEndDate) setLocalEndDate(propEndDate);
  }, [propStartDate, propEndDate]);

  const SORT_OPTIONS = [
    { value: "published_at", label: lang === "ko" ? "최신순" : lang === "zh" ? "最新" : "Latest" },
    { value: "views", label: lang === "ko" ? "조회수순" : lang === "zh" ? "浏览量" : "Views" },
    { value: "likes", label: lang === "ko" ? "좋아요순" : lang === "zh" ? "点赞" : "Likes" },
    { value: "comments", label: lang === "ko" ? "댓글순" : lang === "zh" ? "评论" : "Comments" },
    { value: "engagement_rate", label: lang === "ko" ? "참여율순" : lang === "zh" ? "参与率" : "Engagement" },
  ];

  // Toggle platform selection
  const togglePlatform = (platformId) => {
    setLocalPlatforms(prev => 
      prev.includes(platformId) 
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  // Clear all filters
  const clearFilters = () => {
    setLocalPlatforms([]);
    setLocalStartDate(null);
    setLocalEndDate(null);
  };

  // Fetch posts and accounts
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build posts URL with filters
      let postsUrl = `${API}/posts?limit=500&sort_by=${sortBy}`;
      if (localStartDate) {
        postsUrl += `&start_date=${format(localStartDate, "yyyy-MM-dd")}`;
      }
      if (localEndDate) {
        postsUrl += `&end_date=${format(localEndDate, "yyyy-MM-dd")}`;
      }
      if (localPlatforms.length > 0) {
        postsUrl += `&platform=${localPlatforms.join(",")}`;
      }
      if (selectedAccounts.length > 0) {
        postsUrl += `&account_id=${selectedAccounts.join(",")}`;
      }
      
      const [postsRes, accountsRes] = await Promise.all([
        axios.get(postsUrl),
        axios.get(`${API}/accounts`),
      ]);
      
      setPosts(postsRes.data || []);
      setAccounts(accountsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [localStartDate, localEndDate, localPlatforms, selectedAccounts, sortBy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAnalyzePost = (post) => {
    setSelectedPost(post);
    setAnalysisOpen(true);
  };

  const handleCommentAnalyze = (post) => {
    const params = new URLSearchParams({
      platform: post.platform,
      post_id: post.post_id,
      title: (post.title || "").slice(0, 100),
    });
    navigate(`/comments?${params.toString()}`);
  };

  // Count posts per platform for badges
  const platformCounts = posts.reduce((acc, post) => {
    acc[post.platform] = (acc[post.platform] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border/50">
        <CardHeader className="space-y-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-heading">{t.posts.allPosts}</CardTitle>
            {/* Mobile: compact filter trigger */}
            <div className="flex items-center gap-2">
              {/* Sort - always visible */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="h-9 w-[120px] md:w-[140px]" data-testid="sort-select">
                  <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} data-testid={`sort-${opt.value}`}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Mobile: Combined filter popover */}
              <div className="md:hidden">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9" data-testid="mobile-filter-btn">
                      <Filter className="h-4 w-4" />
                      {(localPlatforms.length > 0 || localStartDate || localEndDate) && (
                        <Badge variant="secondary" className="ml-1 h-4 w-4 p-0 text-[10px] flex items-center justify-center">
                          {(localPlatforms.length > 0 ? 1 : 0) + ((localStartDate || localEndDate) ? 1 : 0)}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-3" align="end">
                    <div className="space-y-4">
                      <div className="text-sm font-medium">{lang === "ko" ? "필터 옵션" : lang === "zh" ? "筛选选项" : "Filter Options"}</div>
                      {/* Platform */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">{lang === "ko" ? "플랫폼" : lang === "zh" ? "平台" : "Platform"}</span>
                          {localPlatforms.length > 0 && (
                            <Button variant="ghost" size="sm" className="h-5 px-1.5 text-xs" onClick={() => setLocalPlatforms([])}>
                              {lang === "ko" ? "초기화" : lang === "zh" ? "清除" : "Clear"}
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                          {ALL_PLATFORMS.map((platform) => (
                            <div key={platform.id} className="flex items-center gap-1.5 p-1.5 rounded cursor-pointer hover:bg-muted/50" onClick={() => togglePlatform(platform.id)}>
                              <Checkbox checked={localPlatforms.includes(platform.id)} className="pointer-events-none h-3.5 w-3.5" />
                              <span className="text-xs" style={{ color: platform.color }}>{platform.name}</span>
                              <span className="text-[10px] text-muted-foreground ml-auto">{platformCounts[platform.id] || 0}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Date */}
                      <div className="space-y-2">
                        <span className="text-xs text-muted-foreground">{lang === "ko" ? "날짜" : lang === "zh" ? "日期" : "Date"}</span>
                        <div className="border rounded-md">
                          <CalendarComponent
                            mode="range"
                            selected={{ from: localStartDate, to: localEndDate }}
                            onSelect={(range) => { setLocalStartDate(range?.from || null); setLocalEndDate(range?.to || null); }}
                            numberOfMonths={1}
                          />
                        </div>
                      </div>
                      {(localPlatforms.length > 0 || localStartDate || localEndDate) && (
                        <Button variant="ghost" size="sm" onClick={clearFilters} className="w-full text-xs text-muted-foreground">
                          <X className="h-3 w-3 mr-1" /> {lang === "ko" ? "필터 초기화" : lang === "zh" ? "清除全部" : "Clear All"}
                        </Button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Desktop: individual filter buttons */}
              <div className="hidden md:flex items-center gap-2">
                <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9" data-testid="platform-filter-btn">
                      <Filter className="h-4 w-4 mr-2" />
                      {lang === "ko" ? "플랫폼" : lang === "zh" ? "平台" : "Platform"}
                      {localPlatforms.length > 0 && (
                        <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                          {localPlatforms.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3" align="end">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {lang === "ko" ? "플랫폼 필터" : lang === "zh" ? "平台筛选" : "Filter by Platform"}
                        </span>
                        {localPlatforms.length > 0 && (
                          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setLocalPlatforms([])}>
                            {lang === "ko" ? "초기화" : lang === "zh" ? "清除" : "Clear"}
                          </Button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {ALL_PLATFORMS.map((platform) => (
                          <div key={platform.id} className="flex items-center justify-between py-1">
                            <label className="flex items-center gap-2 cursor-pointer flex-1" htmlFor={`platform-${platform.id}`}>
                              <Checkbox id={`platform-${platform.id}`} checked={localPlatforms.includes(platform.id)} onCheckedChange={() => togglePlatform(platform.id)} />
                              <span className="text-sm" style={{ color: platform.color }}>{platform.name}</span>
                            </label>
                            <Badge variant="secondary" className="text-xs h-5">{platformCounts[platform.id] || 0}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9" data-testid="date-filter-btn">
                      <Calendar className="h-4 w-4 mr-2" />
                      {localStartDate || localEndDate ? (
                        <span className="text-xs">
                          {localStartDate ? format(localStartDate, "MM/dd") : "..."} - {localEndDate ? format(localEndDate, "MM/dd") : "..."}
                        </span>
                      ) : (
                        <span>{lang === "ko" ? "날짜" : lang === "zh" ? "日期" : "Date"}</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <CalendarComponent
                      mode="range"
                      selected={{ from: localStartDate, to: localEndDate }}
                      onSelect={(range) => { setLocalStartDate(range?.from || null); setLocalEndDate(range?.to || null); }}
                      numberOfMonths={1}
                    />
                    {(localStartDate || localEndDate) && (
                      <div className="p-2 border-t">
                        <Button variant="ghost" size="sm" className="w-full" onClick={() => { setLocalStartDate(null); setLocalEndDate(null); }}>
                          {lang === "ko" ? "날짜 초기화" : lang === "zh" ? "清除日期" : "Clear dates"}
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>

                {(localPlatforms.length > 0 || localStartDate || localEndDate) && (
                  <Button variant="ghost" size="sm" className="h-9 text-muted-foreground hover:text-foreground" onClick={clearFilters}>
                    <X className="h-4 w-4 mr-1" />
                    {lang === "ko" ? "초기화" : lang === "zh" ? "清除" : "Clear"}
                  </Button>
                )}

                <div className="h-6 w-px bg-border mx-1" />
                <Languages className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{t.languageAnalysis.title}</span>
              </div>
            </div>
          </div>
        </CardHeader>
        
        {/* Active Filters Display */}
        {localPlatforms.length > 0 && (
          <div className="px-6 pb-3 flex flex-wrap gap-2">
            {localPlatforms.map(platformId => {
              const platform = ALL_PLATFORMS.find(p => p.id === platformId);
              return (
                <Badge 
                  key={platformId}
                  variant="secondary"
                  className="cursor-pointer hover:bg-destructive/20"
                  onClick={() => togglePlatform(platformId)}
                >
                  <span style={{ color: platform?.color }}>{platform?.name}</span>
                  <X className="h-3 w-3 ml-1" />
                </Badge>
              );
            })}
          </div>
        )}

        <CardContent>
          {/* Results count */}
          <div className="mb-4 text-sm text-muted-foreground">
            {lang === "ko" ? `총 ${posts.length}개의 게시물` : lang === "zh" ? `共 ${posts.length} 条帖子` : `${posts.length} posts total`}
          </div>
          
          <PostsTableWithAnalysis 
            posts={posts} 
            loading={loading} 
            lang={lang} 
            accounts={accounts}
            onAnalyze={handleAnalyzePost}
            onCommentAnalyze={handleCommentAnalyze}
          />
        </CardContent>
      </Card>

      {/* Language Analysis Dialog */}
      <Dialog open={analysisOpen} onOpenChange={setAnalysisOpen}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="truncate max-w-[400px]">{selectedPost?.title || "Post"}</span>
              <Badge variant="outline" className="capitalize">{selectedPost?.platform}</Badge>
            </DialogTitle>
            <DialogDescription>
              {(() => {
                const isChinese = ["douyin", "xiaohongshu", "weibo", "bilibili"].includes(selectedPost?.platform);
                const viewsText = isChinese && selectedPost?.views === 0 ? "N/A" : `${formatNumber(selectedPost?.views || 0)} views`;
                return `${viewsText} • ${formatNumber(selectedPost?.comments || 0)} comments`;
              })()}
            </DialogDescription>
          </DialogHeader>
          {selectedPost && (
            <LanguageAnalysisPanel post={selectedPost} lang={lang} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PostsPageWithAnalysis;
